import {
  isDiagnosticPresenceFlag,
  redactDiagnosticCardNumber,
} from 'src/utils/security/bounded-sanitize';

import { scrubDiagnosticText } from 'src/features/diagnostics/sanitize';

/**
 * Always-on, bounded, in-memory history of Stripe Terminal SDK calls and
 * listener events. Capture is deliberately independent of the debug-overlay
 * toggle so an operator can inspect the calls that led to a failure.
 *
 * Observability must never become part of payment behavior:
 * - wrappers invoke methods with the original client as `this`;
 * - synchronous throws are rethrown unchanged;
 * - promises are returned by identity, with logging attached on a discarded
 *   side branch; and
 * - every capture path catches its own failures.
 */

export type StripeTerminalLogScope =
  | 'sdk'
  | 'token'
  | 'reader-session'
  | 'payment'
  | 'offline-forwarding'
  | 'background';

export type StripeTerminalLogLevel = 'info' | 'success' | 'warning' | 'error';

export interface StripeTerminalLogError {
  name: string;
  code: string | number | null;
  message: string;
  details?: unknown;
}

export interface StripeTerminalLogEntry {
  id: number;
  kind: 'call' | 'event';
  scope: StripeTerminalLogScope;
  method: string;
  level: StripeTerminalLogLevel;
  label: string;
  readerSerialNumber: string | null;
  transactionId: string | null;
  firstAtIso: string;
  lastAtIso: string;
  durationMs: number | null;
  pending: boolean;
  repeatCount: number;
  request?: unknown;
  response?: unknown;
  error: StripeTerminalLogError | null;
  summaryKey: string | null;
}

export const STRIPE_TERMINAL_LOG_LIMIT = 400;

const COALESCE_TAILS_LIMIT = 200;
const MAX_DEPTH = 7;
const MAX_KEYS = 80;
const MAX_ARRAY_ITEMS = 50;
const MAX_STRING_LENGTH = 2_000;
const MAX_ERROR_MESSAGE_LENGTH = 600;
const REDACTED = '[redacted]';

const SENSITIVE_KEY_FRAGMENTS = [
  'apikey',
  'authorization',
  'bearertoken',
  'clientsecret',
  'connectiontoken',
  'credential',
  'password',
  'secret',
  'signingkey',
  'token',
];
const SENSITIVE_EXACT_KEYS = new Set([
  'cardnumber',
  'cvc',
  'cvv',
  'emv',
  'pan',
  'pin',
  'track',
  'track1',
  'track2',
]);
const BEARER_PATTERN = /bearer\s+[a-z0-9._~+/=-]+/gi;
const STRIPE_CLIENT_SECRET_PATTERN = /\bpi_[a-z0-9]+_secret_[a-z0-9_]+\b/gi;
const STRIPE_SECRET_KEY_PATTERN = /\b(?:sk|rk)_(?:test|live)_[a-z0-9_]+\b/gi;
const STRIPE_CONNECTION_TOKEN_PATTERN = /\bpst_(?:test|live)_[a-z0-9_]+\b/gi;

let entries: readonly StripeTerminalLogEntry[] = [];
let nextEntryId = 1;
const listeners = new Set<() => void>();
const pendingStartedAt = new Map<number, number>();
const coalesceTails = new Map<string, { entryId: number; summaryKey: string }>();

function notifyListeners(): void {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // A broken subscriber must never break terminal capture.
    }
  });
}

function isSensitiveKey(key: string): boolean {
  const normalized = key.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (SENSITIVE_EXACT_KEYS.has(normalized)) return true;
  return SENSITIVE_KEY_FRAGMENTS.some((fragment) => normalized.includes(fragment));
}

function sanitizeString(value: string): string {
  const scrubbed = scrubDiagnosticText(
    value
      .replace(BEARER_PATTERN, 'Bearer [redacted]')
      .replace(STRIPE_CLIENT_SECRET_PATTERN, '[stripe-client-secret-redacted]')
      .replace(STRIPE_SECRET_KEY_PATTERN, '[stripe-secret-key-redacted]')
      .replace(STRIPE_CONNECTION_TOKEN_PATTERN, '[stripe-connection-token-redacted]')
  );
  return scrubbed.length > MAX_STRING_LENGTH
    ? `${scrubbed.slice(0, MAX_STRING_LENGTH)}…`
    : scrubbed;
}

function sanitizeValue(value: unknown, depth: number, seen: WeakSet<object>): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') return sanitizeString(value);
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value === 'bigint') return value.toString();
  if (typeof value === 'function') return '[function]';
  if (typeof value === 'symbol') return value.toString();
  if (typeof value !== 'object') return String(value);
  if (value instanceof Date) return value.toISOString();
  if (depth >= MAX_DEPTH) return '[max-depth]';
  if (seen.has(value)) return '[circular]';
  seen.add(value);

  if (Array.isArray(value)) {
    const result = value
      .slice(0, MAX_ARRAY_ITEMS)
      .map((item) => sanitizeValue(item, depth + 1, seen));
    if (value.length > MAX_ARRAY_ITEMS) {
      result.push(`[+${value.length - MAX_ARRAY_ITEMS} more]`);
    }
    return result;
  }

  if (value instanceof Error) {
    const errorResult: Record<string, unknown> = {
      name: sanitizeString(value.name || 'Error'),
      message: sanitizeString(value.message || ''),
    };
    Object.entries(value as unknown as Record<string, unknown>).forEach(([key, item]) => {
      if (key === 'stack') return;
      if (isDiagnosticPresenceFlag(key, item)) errorResult[key] = item;
      else errorResult[key] = isSensitiveKey(key) ? REDACTED : sanitizeValue(item, depth + 1, seen);
    });
    return errorResult;
  }

  const result: Record<string, unknown> = {};
  const pairs = Object.entries(value as Record<string, unknown>);
  pairs.slice(0, MAX_KEYS).forEach(([key, item]) => {
    if (/^(?:cardnumber|pan)$/.test(key.toLowerCase().replace(/[^a-z0-9]/g, ''))) {
      result[key] = redactDiagnosticCardNumber(item);
      return;
    }
    if (isDiagnosticPresenceFlag(key, item)) {
      result[key] = item;
      return;
    }
    result[key] = isSensitiveKey(key) ? REDACTED : sanitizeValue(item, depth + 1, seen);
  });
  if (pairs.length > MAX_KEYS) result['…'] = `[+${pairs.length - MAX_KEYS} more keys]`;
  return result;
}

/** Credentials and card data are removed before a value enters the buffer. */
export function sanitizeStripeTerminalLogValue(value: unknown): unknown {
  try {
    const sanitized = sanitizeValue(value, 0, new WeakSet());
    JSON.stringify(sanitized);
    return sanitized;
  } catch {
    return '[unserializable]';
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readString(value: unknown, key: string): string | null {
  const raw = asRecord(value)?.[key];
  return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
}

function readReaderSerial(value: unknown): string | null {
  const record = asRecord(value);
  if (!record) return null;
  const direct = readString(record, 'serialNumber');
  if (direct) return direct;
  const reader = asRecord(record.reader);
  if (reader) return readString(reader, 'serialNumber');
  const readers = Array.isArray(record.readers) ? record.readers : [];
  return readers.length > 0 ? readString(readers[0], 'serialNumber') : null;
}

function readTransactionId(value: unknown): string | null {
  const record = asRecord(value);
  if (!record) return null;
  return readString(record, 'transactionId') ?? readString(record.metadata, 'transactionId');
}

function toLogError(error: unknown): StripeTerminalLogError {
  try {
    const record = asRecord(error);
    const rawCode = record?.code;
    let code: string | number | null = null;
    if (typeof rawCode === 'string') code = sanitizeString(rawCode).slice(0, 100);
    else if (typeof rawCode === 'number') code = rawCode;
    let message: string;
    if (error instanceof Error) ({ message } = error);
    else if (typeof error === 'string') message = error;
    else message = JSON.stringify(error ?? '') || '';
    const details = sanitizeStripeTerminalLogValue(error);
    return {
      name: error instanceof Error ? sanitizeString(error.name || 'Error').slice(0, 100) : 'Error',
      code,
      message: sanitizeString(message).slice(0, MAX_ERROR_MESSAGE_LENGTH),
      ...(details === undefined ? {} : { details }),
    };
  } catch {
    return { name: 'Error', code: null, message: '[unserializable]' };
  }
}

function normalizeStatus(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim().toUpperCase() : null;
}

function distillCallResult(
  method: string,
  value: unknown
): { level: StripeTerminalLogLevel; summary: string } {
  const record = asRecord(value);
  const status = normalizeStatus(record?.status);
  if (status === 'SUCCEEDED') return { level: 'success', summary: 'succeeded' };
  if (status === 'CANCELED' || status === 'CANCELLED') {
    return { level: 'warning', summary: 'canceled' };
  }
  if (status === 'REQUIRES_PAYMENT_METHOD') {
    return { level: 'warning', summary: 'requires payment method' };
  }
  if (status) return { level: 'info', summary: `status=${status}` };

  if (method === 'discoverReaders') {
    const count = Array.isArray(record?.readers) ? record.readers.length : 0;
    return {
      level: count > 0 ? 'success' : 'warning',
      summary: `${count} reader${count === 1 ? '' : 's'}`,
    };
  }
  if (method === 'getConnectedReader') {
    const serial = readReaderSerial(value);
    return {
      level: serial ? 'success' : 'info',
      summary: serial ? `connected:${serial}` : 'not connected',
    };
  }
  if (method === 'connectReader') return { level: 'success', summary: 'connected' };
  if (method === 'disconnectReader') return { level: 'success', summary: 'disconnected' };
  if (method.startsWith('cancel')) return { level: 'success', summary: 'canceled' };
  if (method === 'acknowledgeForwardedPaymentIntent') {
    return { level: 'success', summary: 'acknowledged' };
  }
  if (method === 'listPendingForwardedPaymentIntents') {
    const count = Array.isArray(record?.payments) ? record.payments.length : 0;
    return {
      level: 'info',
      summary: `${count} pending forwarded payment${count === 1 ? '' : 's'}`,
    };
  }
  return { level: 'info', summary: 'completed' };
}

function distillEvent(
  eventName: string,
  detail: unknown
): { level: StripeTerminalLogLevel; summary: string } {
  const normalized = eventName.toLowerCase();
  const detailRecord = asRecord(detail);
  const sanitizedDetail = JSON.stringify(sanitizeStripeTerminalLogValue(detail)) ?? '';
  if (
    normalized.includes('failed') ||
    normalized.includes('unexpectedreaderdisconnect') ||
    detailRecord?.error !== undefined
  ) {
    return { level: 'error', summary: sanitizeString(sanitizedDetail || eventName) };
  }
  if (normalized.includes('disconnected') || normalized.includes('canceled')) {
    return { level: 'warning', summary: normalized };
  }
  if (
    normalized.includes('connectedreader') ||
    normalized.includes('reconnectsucceeded') ||
    normalized.includes('confirmedpaymentintent') ||
    normalized.includes('collectedpaymentintent') ||
    normalized.includes('paymentintentforwarded')
  ) {
    return { level: 'success', summary: normalized };
  }
  return { level: 'info', summary: sanitizeString(`${normalized}:${sanitizedDetail}`) };
}

function streamKeyOf(
  entry: Pick<
    StripeTerminalLogEntry,
    'kind' | 'scope' | 'method' | 'readerSerialNumber' | 'transactionId'
  >
): string {
  return `${entry.kind}|${entry.scope}|${entry.method}|${entry.readerSerialNumber ?? ''}|${
    entry.transactionId ?? ''
  }`;
}

function pruneCoalesceTails(): void {
  while (coalesceTails.size > COALESCE_TAILS_LIMIT) {
    const oldest = coalesceTails.keys().next().value;
    if (oldest === undefined) return;
    coalesceTails.delete(oldest);
  }
}

function appendEntry(entry: StripeTerminalLogEntry): void {
  const next = [...entries, entry];
  const overflowCount = Math.max(0, next.length - STRIPE_TERMINAL_LOG_LIMIT);
  next.slice(0, overflowCount).forEach((evicted) => pendingStartedAt.delete(evicted.id));
  entries = next.slice(overflowCount);
}

interface BeginStripeTerminalLogInput {
  scope: StripeTerminalLogScope;
  method: string;
  label: string;
  request?: unknown;
  readerSerialNumber?: string | null;
  transactionId?: string | null;
}

function beginStripeTerminalLog(input: BeginStripeTerminalLogInput): number | null {
  try {
    const id = nextEntryId;
    nextEntryId += 1;
    const nowIso = new Date().toISOString();
    const entry: StripeTerminalLogEntry = {
      id,
      kind: 'call',
      scope: input.scope,
      method: input.method,
      level: 'info',
      label: sanitizeString(input.label),
      readerSerialNumber: input.readerSerialNumber
        ? sanitizeString(input.readerSerialNumber)
        : null,
      transactionId: input.transactionId ? sanitizeString(input.transactionId) : null,
      firstAtIso: nowIso,
      lastAtIso: nowIso,
      durationMs: null,
      pending: true,
      repeatCount: 1,
      request:
        input.request === undefined ? undefined : sanitizeStripeTerminalLogValue(input.request),
      error: null,
      summaryKey: null,
    };
    pendingStartedAt.set(id, Date.now());
    appendEntry(entry);
    notifyListeners();
    return id;
  } catch {
    return null;
  }
}

function completeStripeTerminalLog(
  id: number | null,
  result: { value?: unknown; error?: unknown }
): void {
  try {
    if (id === null) return;
    const startedAt = pendingStartedAt.get(id);
    pendingStartedAt.delete(id);
    const index = entries.findIndex((entry) => entry.id === id);
    if (index === -1) return;
    const current = entries[index];
    const nowIso = new Date().toISOString();
    const durationMs = startedAt === undefined ? null : Math.max(0, Date.now() - startedAt);

    let level: StripeTerminalLogLevel;
    let summary: string;
    let error: StripeTerminalLogError | null = null;
    let response: unknown;
    if (result.error !== undefined) {
      error = toLogError(result.error);
      level = 'error';
      summary = `error:${String(error.code ?? error.name)}:${error.message}`;
    } else {
      ({ level, summary } = distillCallResult(current.method, result.value));
      response = result.value === undefined ? null : sanitizeStripeTerminalLogValue(result.value);
    }

    const summaryKey = `${level}|${sanitizeString(summary)}`;
    const readerSerialNumber =
      current.readerSerialNumber ??
      readReaderSerial(result.value) ??
      readReaderSerial(result.error);
    const transactionId =
      current.transactionId ?? readTransactionId(result.value) ?? readTransactionId(result.error);
    const finalized: StripeTerminalLogEntry = {
      ...current,
      readerSerialNumber: readerSerialNumber ? sanitizeString(readerSerialNumber) : null,
      transactionId: transactionId ? sanitizeString(transactionId) : null,
      level,
      pending: false,
      lastAtIso: nowIso,
      durationMs,
      response: response === undefined ? current.response : response,
      error,
      summaryKey,
    };
    const streamKey = streamKeyOf(finalized);
    const tail = coalesceTails.get(streamKey);
    if (tail && tail.summaryKey === finalized.summaryKey && tail.entryId !== id) {
      const tailIndex = entries.findIndex((entry) => entry.id === tail.entryId);
      if (tailIndex !== -1) {
        const next = entries.filter((entry) => entry.id !== id);
        const mergedIndex = next.findIndex((entry) => entry.id === tail.entryId);
        next[mergedIndex] = {
          ...next[mergedIndex],
          lastAtIso: nowIso,
          durationMs,
          repeatCount: next[mergedIndex].repeatCount + 1,
          request: finalized.request,
          response: finalized.response,
          error: finalized.error,
        };
        entries = next;
        notifyListeners();
        return;
      }
    }

    const next = entries.slice();
    next[index] = finalized;
    entries = next;
    coalesceTails.set(streamKey, { entryId: id, summaryKey });
    pruneCoalesceTails();
    notifyListeners();
  } catch {
    // Capture must never throw into terminal code.
  }
}

export interface StripeTerminalLogEventInput {
  scope: StripeTerminalLogScope;
  eventName: string;
  detail?: unknown;
  level?: StripeTerminalLogLevel;
  readerSerialNumber?: string | null;
  transactionId?: string | null;
  label?: string;
}

export function logStripeTerminalEvent(input: StripeTerminalLogEventInput): void {
  try {
    const distilled = distillEvent(input.eventName, input.detail);
    const level = input.level ?? distilled.level;
    const { summary } = distilled;
    const nowIso = new Date().toISOString();
    const rawReaderSerialNumber = input.readerSerialNumber ?? readReaderSerial(input.detail);
    const rawTransactionId = input.transactionId ?? readTransactionId(input.detail);
    const readerSerialNumber = rawReaderSerialNumber ? sanitizeString(rawReaderSerialNumber) : null;
    const transactionId = rawTransactionId ? sanitizeString(rawTransactionId) : null;
    const method = sanitizeString(input.eventName);
    const label = sanitizeString(input.label ?? `event ← ${input.eventName}`);
    const summaryKey = `${level}|${summary}`;
    const streamKey = `event|${input.scope}|${method}|${readerSerialNumber ?? ''}|${
      transactionId ?? ''
    }`;
    const tail = coalesceTails.get(streamKey);
    if (tail?.summaryKey === summaryKey) {
      const tailIndex = entries.findIndex((entry) => entry.id === tail.entryId);
      if (tailIndex !== -1) {
        const next = entries.slice();
        next[tailIndex] = {
          ...next[tailIndex],
          lastAtIso: nowIso,
          repeatCount: next[tailIndex].repeatCount + 1,
          response: sanitizeStripeTerminalLogValue(input.detail),
        };
        entries = next;
        notifyListeners();
        return;
      }
    }

    const id = nextEntryId;
    nextEntryId += 1;
    const entry: StripeTerminalLogEntry = {
      id,
      kind: 'event',
      scope: input.scope,
      method,
      level,
      label,
      readerSerialNumber: readerSerialNumber ?? null,
      transactionId: transactionId ?? null,
      firstAtIso: nowIso,
      lastAtIso: nowIso,
      durationMs: null,
      pending: false,
      repeatCount: 1,
      response: sanitizeStripeTerminalLogValue(input.detail),
      error: null,
      summaryKey,
    };
    appendEntry(entry);
    coalesceTails.set(streamKey, { entryId: id, summaryKey });
    pruneCoalesceTails();
    notifyListeners();
  } catch {
    // Capture must never throw into listener code.
  }
}

export function subscribeStripeTerminalLog(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getStripeTerminalLogSnapshot(): readonly StripeTerminalLogEntry[] {
  return entries;
}

export function clearStripeTerminalLog(): void {
  try {
    entries = [];
    pendingStartedAt.clear();
    coalesceTails.clear();
    notifyListeners();
  } catch {
    // Capture must never throw.
  }
}

function describeCall(method: string, request: unknown): string {
  const record = asRecord(request);
  if (method === 'addListener') return `addListener → ${readString(record, 'eventName') ?? '—'}`;
  if (method === 'discoverReaders') {
    return `discoverReaders → ${readString(record, 'locationId') ?? 'unscoped'} (${String(
      record?.type ?? 'unknown'
    )})`;
  }
  if (method === 'connectReader') {
    return `connectReader → ${readReaderSerial(request) ?? 'unknown reader'}`;
  }
  if (method === 'createPaymentIntent') {
    const amount = typeof record?.amountCents === 'number' ? record.amountCents : null;
    return `createPaymentIntent${amount === null ? '' : ` $${(amount / 100).toFixed(2)}`}`;
  }
  if (method === 'processPaymentIntent') {
    return `processPaymentIntent tx=${readTransactionId(request) ?? '—'}`;
  }
  if (method === 'acknowledgeForwardedPaymentIntent') {
    return `acknowledgeForwardedPaymentIntent tx=${readTransactionId(request) ?? '—'}`;
  }
  return method;
}

function requestForCall(method: string, args: unknown[]): unknown {
  if (method === 'addListener') return { eventName: String(args[0] ?? '') };
  if (args.length === 0) return undefined;
  if (args.length === 1) return args[0];
  return args;
}

function logStripeTerminalCall<T>(
  scope: StripeTerminalLogScope,
  method: string,
  args: unknown[],
  run: () => T
): T {
  let id: number | null = null;
  try {
    const request = requestForCall(method, args);
    id = beginStripeTerminalLog({
      scope,
      method,
      label: describeCall(method, request),
      request,
      readerSerialNumber: readReaderSerial(request),
      transactionId: readTransactionId(request),
    });
  } catch {
    // Argument inspection must never prevent the SDK call.
  }
  let result: T;
  try {
    result = run();
  } catch (error) {
    completeStripeTerminalLog(id, { error });
    throw error;
  }
  if (result && typeof (result as unknown as { then?: unknown }).then === 'function') {
    try {
      (result as unknown as Promise<unknown>)
        .then(
          (value) => completeStripeTerminalLog(id, { value }),
          (error) => completeStripeTerminalLog(id, { error })
        )
        .catch(() => undefined);
    } catch {
      // Logging must never affect the underlying call.
    }
  } else {
    completeStripeTerminalLog(id, { value: result });
  }
  return result;
}

export interface StripeTerminalInstrumentationOptions {
  scope: StripeTerminalLogScope;
}

const STRIPE_TERMINAL_METHODS = [
  'initialize',
  'isTapToPayAccountLinked',
  'discoverReaders',
  'setConnectionToken',
  'setSimulatorConfiguration',
  'connectReader',
  'getConnectedReader',
  'disconnectReader',
  'clearCachedCredentials',
  'cancelDiscoverReaders',
  'createPaymentIntent',
  'processPaymentIntent',
  'cancelProcessPaymentIntent',
  'collectPaymentMethod',
  'cancelCollectPaymentMethod',
  'confirmPaymentIntent',
  'installAvailableUpdate',
  'cancelInstallUpdate',
  'setReaderDisplay',
  'clearReaderDisplay',
  'rebootReader',
  'cancelReaderReconnection',
  'getOfflineStatus',
  'listPendingForwardedPaymentIntents',
  'acknowledgeForwardedPaymentIntent',
  'setTapToPayUxConfiguration',
  'addListener',
] as const;

/**
 * Return a method-compatible facade that records calls and listener payloads.
 * If facade construction fails, the untouched client is returned.
 */
export function instrumentStripeTerminalClient<T extends object>(
  client: T,
  options: StripeTerminalInstrumentationOptions
): T {
  try {
    const wrapped: Record<string, unknown> = {};
    STRIPE_TERMINAL_METHODS.forEach((method) => {
      const rawMethod = (client as Record<string, unknown>)[method];
      if (typeof rawMethod !== 'function') return;
      wrapped[method] = (...originalArgs: unknown[]) => {
        let args = originalArgs;
        if (method === 'addListener' && typeof originalArgs[1] === 'function') {
          try {
            const eventName = String(originalArgs[0] ?? 'unknown');
            const listener = originalArgs[1] as (...eventArgs: unknown[]) => unknown;
            const instrumentedListener = function instrumentedStripeTerminalListener(
              this: unknown,
              ...eventArgs: unknown[]
            ) {
              const detail = eventArgs.length <= 1 ? eventArgs[0] : eventArgs;
              logStripeTerminalEvent({ scope: options.scope, eventName, detail });
              return listener.apply(this, eventArgs);
            };
            args = [originalArgs[0], instrumentedListener, ...originalArgs.slice(2)];
          } catch {
            // Listener instrumentation must not prevent native registration.
          }
        }
        return logStripeTerminalCall(options.scope, method, originalArgs, () =>
          (rawMethod as (...methodArgs: unknown[]) => unknown).apply(client, args)
        );
      };
    });
    return wrapped as T;
  } catch {
    return client;
  }
}
